import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import supabase from '../../utils/supabaseClient';
import LoadingScreen from '../../components/LoadingScreen';
import pptxgen from "pptxgenjs";
import PostPitchDeckButton from '../../components/gallery/PostPitchDeckButton';
import PDFViewer from '../../components/PDFViewer';

// Wrapper for PostPitchDeckButton to handle table name mismatch
const PostToGalleryButton = ({ pitchDeck }) => {
  const [adaptedPitchDeck, setAdaptedPitchDeck] = useState(null);
  
  useEffect(() => {
    // This function creates a modified pitch deck object that can work with the gallery
    const adaptPitchDeck = async () => {
      if (!pitchDeck) return;
      
      try {
        // First check if the pitch deck already exists in the pitch_decks table
        const { data, error } = await supabase
          .from('pitch_decks')
          .select('*')
          .eq('product_id', pitchDeck.product_id)
          .limit(1);
          
        if (!error && data && data.length > 0) {
          // Pitch deck exists in pitch_decks table, use that
          setAdaptedPitchDeck(data[0]);
        } else {
          // Need to create a compatible pitch deck in the pitch_decks table
          const { data: insertData, error: insertError } = await supabase
            .from('pitch_decks')
            .insert({
              id: pitchDeck.id,  // Use the same ID
              name: pitchDeck.name || 'Untitled Pitch Deck',
              product_id: pitchDeck.product_id,
              user_id: pitchDeck.user_id,
              content: { slides: pitchDeck.slides } // Convert to expected format
            })
            .select()
            .single();
            
          if (insertError) {
            console.error('Error creating compatible pitch deck:', insertError);
            return;
          }
          
          setAdaptedPitchDeck(insertData);
        }
      } catch (err) {
        console.error('Error adapting pitch deck for gallery:', err);
      }
    };
    
    adaptPitchDeck();
  }, [pitchDeck]);
  
  if (!adaptedPitchDeck) {
    return (
      <button
        disabled
        className="flex items-center px-4 py-2 bg-gray-100 text-gray-400 rounded cursor-not-allowed"
      >
        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
        </svg>
        Preparing...
      </button>
    );
  }
  
  return <PostPitchDeckButton pitchDeck={adaptedPitchDeck} />;
};

// Modify the ImageUploader component to use the correct Supabase bucket
const ImageUploader = ({ onImageUpload, buttonText, accept = "image/*", className = "" }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleUpload = async (e) => {
    setError(null);
    try {
      setUploading(true);
      const file = e.target.files[0];
      if (!file) return;
      
      // Maximum file size (5MB)
      const MAX_FILE_SIZE = 5 * 1024 * 1024;
      
      if (file.size > MAX_FILE_SIZE) {
        throw new Error(`File size exceeds limit (5MB). Current size: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      }

      // Create a unique file name
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2, 15)}_${Date.now()}.${fileExt}`;
      const filePath = `slides/${fileName}`;
      
      console.log('Uploading to bucket: pitch_deck_assets');
      
      // Upload to Supabase Storage using the correct bucket
      const { error: uploadError } = await supabase.storage
        .from('pitch_deck_assets')
        .upload(filePath, file);

      if (uploadError) {
        console.error('Error uploading to Supabase:', uploadError);
        throw new Error(`Upload failed: ${uploadError.message}`);
      }
      
      // Get public URL
      const { data: urlData } = supabase.storage
        .from('pitch_deck_assets')
        .getPublicUrl(filePath);
      
      if (!urlData || !urlData.publicUrl) {
        throw new Error('Failed to get public URL for uploaded file');
      }
      
      console.log('Upload successful, URL:', urlData.publicUrl);
      onImageUpload(urlData.publicUrl);
      
    } catch (error) {
      console.error('Error uploading image:', error);
      setError(`Error uploading image: ${error.message || 'Unknown error'}`);
      
      // Fall back to base64 if storage upload fails
      try {
        console.log('Falling back to base64 encoding');
        const reader = new FileReader();
        reader.onload = (event) => {
          const dataUrl = event.target.result;
          onImageUpload(dataUrl);
          setUploading(false);
        };
        reader.onerror = () => {
          setError('Failed to process image as data URL');
          setUploading(false);
        };
        reader.readAsDataURL(e.target.files[0]);
        return;
      } catch (fallbackError) {
        console.error('Base64 fallback also failed:', fallbackError);
      }
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className={className}>
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept={accept}
        onChange={handleUpload}
      />
      <div className="flex flex-col">
        <button
          type="button"
          onClick={() => fileInputRef.current.click()}
          disabled={uploading}
          className="flex items-center px-3 py-1.5 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 text-sm font-medium"
        >
          {uploading ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Uploading...
            </>
          ) : (
            <>
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0l-4 4m4-4v12" />
              </svg>
              {buttonText}
            </>
          )}
        </button>
        {error && (
          <div className="text-red-500 text-xs mt-1">{error}</div>
        )}
      </div>
    </div>
  );
};

// Update the UploadedDocumentViewer component for better PDF handling
const UploadedDocumentViewer = ({ fileUrl, fileType }) => {
  if (!fileUrl) return null;
  
  // Display based on file type
  if (fileType === 'pdf') {
    return (
      <div className="w-full h-full flex flex-col overflow-hidden">
        <PDFViewer pdfUrl={fileUrl} />
      </div>
    );
  }
    
  // For PowerPoint files, immediately show the fallback view
  return (
    <div className="w-full h-full flex flex-col items-center justify-center p-8 text-center">
      <div className="bg-gray-50 p-8 rounded-lg shadow-sm max-w-2xl w-full">
        <svg className="w-20 h-20 text-blue-500 mb-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <h3 className="text-xl font-semibold mb-2">PowerPoint Presentation</h3>
        <p className="text-gray-600 mb-6">
          This presentation cannot be previewed directly in the browser due to security restrictions.
        </p>
        <div className="flex flex-col space-y-4">
          <a 
            href={fileUrl} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download Presentation
          </a>
        </div>
      </div>
    </div>
  );
};

const PitchDeckViewPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [pitchDeck, setPitchDeck] = useState(null);
  const [product, setProduct] = useState(null);
  const [error, setError] = useState(null);
  const [userPitchDecks, setUserPitchDecks] = useState([]);
  const [showUserDecks, setShowUserDecks] = useState(false);
  
  // Add slide navigation state
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const slideInterval = useRef(null);
  
  // Parse query params to check for edit mode
  const queryParams = new URLSearchParams(location.search);
  const isEditMode = queryParams.get('edit') === 'true';
  
  // State for editing
  const [editableSlides, setEditableSlides] = useState(null);
  const [isEditing, setIsEditing] = useState(isEditMode);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [currentSlideImages, setCurrentSlideImages] = useState([]);
  const [isUploadedDocument, setIsUploadedDocument] = useState(false);

  // Add data fetching
  useEffect(() => {
    const fetchPitchDeck = async () => {
      try {
        console.log('Fetching pitch deck with ID:', id);
        
        // Validate ID format (assuming UUID format)
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (!id || !uuidRegex.test(id)) {
          throw new Error('Invalid pitch deck ID format');
        }

        // Only try the tables that actually exist
        const tablesToTry = ['pitch_decks'];
        let pitchDeckData = null;
        let tableFound = '';

        // First try to find by ID (primary key)
        for (const tableName of tablesToTry) {
          console.log(`Trying to fetch from table: ${tableName} by id`);
          
          try {
            const { data, error } = await supabase
              .from(tableName)
              .select('*')
              .eq('id', id);
              
            if (error) {
              console.log(`Error with table ${tableName}:`, error);
              continue;
            }
            
            console.log(`Results from ${tableName} by id:`, data);
            
            if (data && data.length > 0) {
              pitchDeckData = data;
              tableFound = tableName;
              console.log(`Found pitch deck in table: ${tableName} by id`);
              break;
            }
          } catch (err) {
            console.log(`Failed to query ${tableName}:`, err);
          }
        }
        
        // If not found by ID, try by product_id (this is what we need according to your explanation)
        if (!pitchDeckData) {
          console.log('Not found by id, trying by product_id');
          
          for (const tableName of tablesToTry) {
            try {
              console.log(`Trying to fetch from table: ${tableName} by product_id`);
              
              const { data, error } = await supabase
                .from(tableName)
                .select('*')
                .eq('product_id', id);
                
              if (error) {
                console.log(`Error with table ${tableName} by product_id:`, error);
                continue;
              }
              
              console.log(`Results from ${tableName} by product_id:`, data);
              
              if (data && data.length > 0) {
                pitchDeckData = data;
                tableFound = `${tableName} (via product_id)`;
                console.log(`Found pitch deck in table: ${tableFound}`);
                break;
              }
            } catch (err) {
              console.log(`Failed to query ${tableName} by product_id:`, err);
            }
          }
        }
        
        if (!pitchDeckData || pitchDeckData.length === 0) {
          console.error('Pitch deck not found in any table');
          throw new Error('Pitch deck not found. Please check if the ID is correct. The ID may be a product ID rather than a pitch deck ID.');
        }
        
        // Get the first result
        const deckData = pitchDeckData[0];
        console.log('Using pitch deck data:', deckData);
        console.log('Found in table:', tableFound);
        setPitchDeck(deckData);
        
        // Fetch associated product - if we found by product_id, we already know the product ID
        if (tableFound.includes('product_id')) {
          // We found by product_id, so the id is the product_id
          const { data: productData, error: productError } = await supabase
            .from('products')
            .select('*')
            .eq('id', id);
            
          if (productError) {
            console.error('Error fetching product:', productError);
          } else if (productData && productData.length > 0) {
            setProduct(productData[0]);
          }
        } else if (deckData.product_id) {
          // We found by other means, use the product_id from the deck
          const { data: productData, error: productError } = await supabase
            .from('products')
            .select('*')
            .eq('id', deckData.product_id);
            
          if (productError) {
            console.error('Error fetching product:', productError);
          } else if (productData && productData.length > 0) {
            setProduct(productData[0]);
          }
        }

        // Check if the pitch deck is an uploaded document
        if (deckData.is_uploaded && deckData.file_url) {
          // Set a flag to indicate this is an uploaded document
          setIsUploadedDocument(true);
        }
      } catch (error) {
        console.error('Error fetching pitch deck:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    
    if (id) {
      fetchPitchDeck();
    } else {
      setError('No pitch deck ID provided');
      setLoading(false);
    }
    
    // Clear any slide intervals when component unmounts
    return () => {
      if (slideInterval.current) {
        clearInterval(slideInterval.current);
      }
    };
  }, [id, user]);

  // Add handleDownload function
  const handleDownload = useCallback(() => {
    // Get the slides based on where they are stored
    const slidesObj = pitchDeck.slides || (pitchDeck.content ? pitchDeck.content.slides : null);
    
    if (!slidesObj) {
      return;
    }

    try {
      // Create a new PowerPoint presentation
      const pptx = new pptxgen();
      
      // Set the presentation properties
      pptx.layout = 'LAYOUT_16x9';
      pptx.title = pitchDeck.name || 'Pitch Deck';
      pptx.subject = product?.name || '';
      pptx.company = user?.profile?.company || '';
      pptx.author = user?.profile?.name || user?.email || '';
      
      // Extract slides from slides object
      const slideOrder = slidesObj.slide_order || [];
      
      // Process each slide
      slideOrder.forEach(slideKey => {
        if (slidesObj[slideKey]) {
          const slide = slidesObj[slideKey];
          const pptxSlide = pptx.addSlide();
          
          pptxSlide.addText(slide.title || slideKey, { 
            x: 0.5, 
            y: 0.5, 
            w: '90%', 
            h: 1, 
            fontSize: 24, 
            bold: true,
            color: '363636' 
          });
          
          // Add content if available
          if (slide.content) {
            pptxSlide.addText('Content was created in HTML and may not appear exactly as designed.', { 
              x: 0.5, 
              y: 2, 
              w: '90%', 
              h: 1, 
              fontSize: 14,
              color: '666666' 
            });
          }
        }
      });
      
      // Save the presentation
      pptx.writeFile({ fileName: `${pitchDeck.name || 'Pitch_Deck'}.pptx` });
    } catch (error) {
      console.error('Error generating PowerPoint:', error);
      alert('Failed to generate PowerPoint file. Please try again.');
    }
  }, [pitchDeck, product, user]);
  
  // Helper to get slide keys in order
  const getOrderedSlideKeys = useCallback(() => {
    if (!pitchDeck) return [];
    
    // Handle pitch decks with slides directly in the pitchDeck object
    if (pitchDeck.slides) {
      const slideOrder = pitchDeck.slides.slide_order || [];
      if (slideOrder.length > 0) return slideOrder;
      
      // Fallback if no slide_order exists
      return Object.keys(pitchDeck.slides).filter(key => 
        key !== 'slide_order' && 
        key !== 'updated_at' && 
        key !== 'background_fit' &&
        key !== 'background_image'
      );
    }
    
    // Handle pitch decks where slides might be in the content object
    if (pitchDeck.content && pitchDeck.content.slides) {
      const slides = pitchDeck.content.slides;
      const slideOrder = slides.slide_order || [];
      if (slideOrder.length > 0) return slideOrder;
      
      // Fallback if no slide_order exists
      return Object.keys(slides).filter(key => 
        key !== 'slide_order' && 
        key !== 'updated_at' && 
        key !== 'background_fit' &&
        key !== 'background_image'
      );
    }
    
    return [];
  }, [pitchDeck]);
  
  // Add slide navigation functions
  const startSlideshow = useCallback(() => {
    const slideKeys = getOrderedSlideKeys();
    if (slideKeys.length === 0) return;
    
    setIsPlaying(true);
    slideInterval.current = setInterval(() => {
      setCurrentSlide(prevSlide => {
        const nextSlide = prevSlide + 1;
        if (nextSlide >= slideKeys.length) {
          clearInterval(slideInterval.current);
          setIsPlaying(false);
          return prevSlide;
        }
        return nextSlide;
      });
    }, 5000); // 5 seconds per slide
  }, [getOrderedSlideKeys]);

  const stopSlideshow = useCallback(() => {
    setIsPlaying(false);
    if (slideInterval.current) {
      clearInterval(slideInterval.current);
    }
  }, []);

  const handlePrevSlide = useCallback(() => {
    stopSlideshow();
    setCurrentSlide(prev => Math.max(0, prev - 1));
  }, [stopSlideshow]);

  const handleNextSlide = useCallback(() => {
    stopSlideshow();
    const slideKeys = getOrderedSlideKeys();
    setCurrentSlide(prev => Math.min(slideKeys.length - 1, prev + 1));
  }, [stopSlideshow, getOrderedSlideKeys]);
  
  const toggleFullScreen = useCallback(() => {
    setIsFullScreen(prev => !prev);
  }, []);

  // Function to fetch all user's pitch decks for recovery
  const fetchUserPitchDecks = useCallback(async () => {
    if (!user) return;
    try {
      const tables = ['pitch_decks'];
      let allDecks = [];
      
      for (const table of tables) {
        const { data, error } = await supabase
          .from(table)
          .select('id, name, created_at, product_id')
          .eq('user_id', user.id);
          
        if (!error && data && data.length > 0) {
          console.log(`Found ${data.length} pitch decks in ${table}`);
          allDecks = [...allDecks, ...data.map(d => ({...d, table}))];
        }
      }
      
      // If we have decks, fetch their associated products for better display
      if (allDecks.length > 0) {
        const productIds = [...new Set(allDecks.filter(d => d.product_id).map(d => d.product_id))];
        
        if (productIds.length > 0) {
          const { data: products } = await supabase
            .from('products')
            .select('id, name')
            .in('id', productIds);
            
          if (products) {
            allDecks = allDecks.map(deck => ({
              ...deck,
              product_name: products.find(p => p.id === deck.product_id)?.name || 'Unknown Product'
            }));
          }
        }
      }
      
      setUserPitchDecks(allDecks);
      setShowUserDecks(true);
    } catch (err) {
      console.error('Error fetching user pitch decks:', err);
    }
  }, [user]);

  // Function to handle saving edited pitch deck
  const handleSavePitchDeck = async () => {
    if (!pitchDeck || !editableSlides) return;
    
    try {
      setIsSaving(true);
      
      // Determine where to save the slides based on the original structure
      let updateData = {};
      
      if (pitchDeck.slides) {
        // If the original pitch deck had direct slides field, use that
        updateData = {
          slides: editableSlides,
          updated_at: new Date().toISOString()
        };
      } else if (pitchDeck.content) {
        // If the original pitch deck had content.slides, preserve that structure
        updateData = {
          content: {
            ...pitchDeck.content,
            slides: editableSlides
          },
          updated_at: new Date().toISOString()
        };
      } else {
        // If neither structure exists, use the content.slides approach (newer format)
        updateData = {
          content: { slides: editableSlides },
          updated_at: new Date().toISOString()
        };
      }
      
      // Update the pitch deck in the database
      const { error: updateError } = await supabase
        .from('pitch_decks')
        .update(updateData)
        .eq('id', pitchDeck.id);
        
      if (updateError) throw updateError;
      
      // Update the local state
      if (pitchDeck.slides) {
        setPitchDeck({
          ...pitchDeck,
          slides: editableSlides,
          updated_at: new Date().toISOString()
        });
      } else {
        setPitchDeck({
          ...pitchDeck,
          content: {
            ...pitchDeck.content,
            slides: editableSlides
          },
          updated_at: new Date().toISOString()
        });
      }
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      
      // Exit edit mode
      setIsEditing(false);
    } catch (err) {
      console.error('Error saving pitch deck:', err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Function to update slide content
  const updateSlideContent = (slideKey, updatedContent) => {
    if (!editableSlides) return;
    
    setEditableSlides({
      ...editableSlides,
      [slideKey]: {
        ...editableSlides[slideKey],
        ...updatedContent
      }
    });
  };
  
  // Get the ordered slide keys
  const orderedSlideKeys = getOrderedSlideKeys();
  const totalSlides = orderedSlideKeys.length;
  
  // Get the current slide content based on the current slide index
  const currentSlideKey = totalSlides > 0 ? orderedSlideKeys[currentSlide] : null;
  const currentSlideContent = currentSlideKey ? (
    pitchDeck.slides ? 
      pitchDeck.slides[currentSlideKey] : 
      (pitchDeck.content && pitchDeck.content.slides ? 
        pitchDeck.content.slides[currentSlideKey] : 
        null)
  ) : null;

  // Update slide images functionality
  const addSlideImage = (slideKey, imageUrl) => {
    if (!editableSlides) return;
    
    const existingImages = editableSlides[slideKey]?.images || [];
    const newImageObject = {
      url: imageUrl,
      width: '50%',    // Default width
      position: 'center', // Default position (center, left, right)
      float: 'none',   // Default float style (none, left, right)
      margin: '10px'   // Default margin
    };
    
    setEditableSlides({
      ...editableSlides,
      [slideKey]: {
        ...editableSlides[slideKey],
        images: [...existingImages, newImageObject]
      }
    });
    
    // Update the current slide images for UI display
    if (currentSlideKey === slideKey) {
      setCurrentSlideImages([...existingImages, newImageObject]);
    }
  };
  
  const removeSlideImage = (slideKey, imageIndex) => {
    if (!editableSlides) return;
    
    const existingImages = [...(editableSlides[slideKey]?.images || [])];
    existingImages.splice(imageIndex, 1);
    
    setEditableSlides({
      ...editableSlides,
      [slideKey]: {
        ...editableSlides[slideKey],
        images: existingImages
      }
    });
    
    // Update the current slide images for UI display
    if (currentSlideKey === slideKey) {
      setCurrentSlideImages(existingImages);
    }
  };

  // Add helper function to convert legacy image format
  const getImageData = (img, idx) => {
    if (typeof img === 'string') {
      return { url: img, width: '50%', position: 'center', float: 'none', margin: '10px', idx };
    }
    return { ...img, idx };
  };
  
  // Update how alignment is applied when the user updates image properties
  const updateImageProperties = (slideKey, imageIndex, properties) => {
    if (!editableSlides) return;
    
    const existingImages = [...(editableSlides[slideKey]?.images || [])];
    
    const updatedImages = existingImages.map((img, idx) => {
      const imageObj = typeof img === 'string' 
        ? { url: img, width: '50%', position: 'center', float: 'none', margin: '10px' }
        : { ...img };
      
      if (idx === imageIndex) {
        // If changing position/alignment, update both position and textAlign properties
        const updatedProps = { ...properties };
        if (properties.position) {
          updatedProps.textAlign = properties.position;
        }
        return { ...imageObj, ...updatedProps };
      }
      return imageObj;
    });
    
    setEditableSlides({
      ...editableSlides,
      [slideKey]: {
        ...editableSlides[slideKey],
        images: updatedImages
      }
    });
    
    // Update the current slide images for UI display
    if (currentSlideKey === slideKey) {
      setCurrentSlideImages(updatedImages);
    }
  };
  
  // Initialize editable slides when pitch deck data is loaded
  useEffect(() => {
    if (pitchDeck && isEditMode) {
      const slidesObj = pitchDeck.slides || (pitchDeck.content && pitchDeck.content.slides);
      if (slidesObj) {
        setEditableSlides(slidesObj);
        setIsEditing(true);
      }
    }
  }, [pitchDeck, isEditMode]);

  // Add back the background image functionality
  const setBackgroundImage = (imageUrl) => {
    if (!editableSlides) return;
    
    setEditableSlides({
      ...editableSlides,
      background_image: imageUrl
    });
  };

  const setBackgroundFit = (fit) => {
    if (!editableSlides) return;
    
    setEditableSlides({
      ...editableSlides,
      background_fit: fit
    });
  };

  // Update current slide images when changing slides
  useEffect(() => {
    if (pitchDeck && currentSlideKey) {
      let slideImages = [];
      
      if (isEditing && editableSlides?.[currentSlideKey]?.images) {
        slideImages = editableSlides[currentSlideKey].images;
      } else if (pitchDeck.slides?.[currentSlideKey]?.images) {
        slideImages = pitchDeck.slides[currentSlideKey].images;
      } else if (pitchDeck.content?.slides?.[currentSlideKey]?.images) {
        slideImages = pitchDeck.content.slides[currentSlideKey].images;
      }
      
      setCurrentSlideImages(slideImages || []);
    }
  }, [currentSlideKey, pitchDeck, isEditing, editableSlides]);

  if (loading) {
    return <LoadingScreen message="Loading pitch deck..." />;
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="mb-6">{error}</p>
          
          {user && !showUserDecks && (
            <button
              onClick={fetchUserPitchDecks}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              View My Available Pitch Decks
            </button>
          )}
          
          {showUserDecks && (
            <div className="mt-8">
              <h3 className="text-lg font-semibold mb-4">Your Pitch Decks</h3>
              {userPitchDecks.length === 0 ? (
                <p>No pitch decks found in your account.</p>
              ) : (
                <div className="space-y-3">
                  {userPitchDecks.map(deck => (
                    <div key={deck.id} className="border rounded p-3 hover:bg-gray-50">
                      <div className="flex justify-between">
                        <div>
                          <h4 className="font-medium">{deck.name || 'Untitled Deck'}</h4>
                          <p className="text-sm text-gray-600">
                            {deck.product_name ? `Product: ${deck.product_name}` : 'No associated product'}
                          </p>
                          <p className="text-sm text-gray-500">
                            Created: {new Date(deck.created_at).toLocaleDateString()}
                          </p>
                          <div className="mt-1 text-xs text-gray-400">
                            <p>Deck ID: {deck.id}</p>
                            {deck.product_id && <p>Product ID: {deck.product_id}</p>}
                            <p>Table: {deck.table}</p>
                          </div>
                        </div>
                        <div className="flex flex-col space-y-2">
                          <a 
                            href={`/pitchdeck/view/${deck.id}`}
                            className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 self-start"
                          >
                            View by Deck ID
                          </a>
                          {deck.product_id && (
                            <a 
                              href={`/pitchdeck/view/${deck.product_id}`}
                              className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 self-start"
                            >
                              View by Product ID
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!pitchDeck) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <h2 className="text-2xl font-bold">Pitch Deck Not Found</h2>
      </div>
    );
  }
  
  // Render with edit mode UI if in edit mode
  const renderEditableSlide = (slideKey, slideContent) => {
    if (!slideContent) return null;
    
    // Add handling for uploaded document slides
    if (slideContent.type === 'Uploaded') {
      return (
        <div className="h-full">
          <UploadedDocumentViewer 
            fileUrl={slideContent.content.file_url} 
            fileType={slideContent.content.file_type}
          />
        </div>
      );
    }
    
    return (
      <div className="w-full h-full p-6">
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Slide Title
          </label>
          <input 
            type="text"
            value={editableSlides[slideKey].title || ''}
            onChange={(e) => updateSlideContent(slideKey, { title: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Slide Content
          </label>
          <textarea
            value={editableSlides[slideKey].content || ''}
            onChange={(e) => updateSlideContent(slideKey, { content: e.target.value })}
            rows={10}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          ></textarea>
        </div>
        
        {/* Image Upload Section */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Slide Images
            </label>
            <ImageUploader 
              onImageUpload={(imageUrl) => addSlideImage(slideKey, imageUrl)}
              buttonText="Add Image"
            />
          </div>
          
          {/* Display current images with edit controls */}
          <div className="space-y-6 mt-4">
            {currentSlideImages.map((img, idx) => {
              const imageData = getImageData(img, idx);
              return (
                <div key={idx} className="border rounded p-4 bg-gray-50">
                  <div className="flex flex-col md:flex-row gap-4">
                    {/* Image preview */}
                    <div className="relative md:w-1/3 group">
                      <div className="w-full" style={{ textAlign: imageData.position || 'center' }}>
                        <img 
                          src={imageData.url} 
                          alt={`Slide ${idx + 1}`} 
                          className="rounded inline-block"
                          style={{
                            width: imageData.width,
                            margin: imageData.margin,
                          }}
                        />
                      </div>
                      <button
                        onClick={() => removeSlideImage(slideKey, idx)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove image"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    
                    {/* Image controls */}
                    <div className="md:w-2/3 space-y-3">
                      <h4 className="font-medium text-sm text-gray-700">Image Properties</h4>
                      
                      {/* Size control */}
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Size</label>
                        <select
                          value={imageData.width}
                          onChange={(e) => updateImageProperties(slideKey, idx, { width: e.target.value })}
                          className="block w-full pl-3 pr-10 py-1.5 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                        >
                          <option value="25%">Small (25%)</option>
                          <option value="50%">Medium (50%)</option>
                          <option value="75%">Large (75%)</option>
                          <option value="100%">Full Width (100%)</option>
                        </select>
                      </div>
                      
                      {/* Position control */}
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Alignment</label>
                        <div className="flex space-x-3">
                          <button
                            type="button"
                            onClick={() => updateImageProperties(slideKey, idx, { float: 'left', position: 'left' })}
                            className={`p-2 rounded-md ${imageData.position === 'left' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}
                            title="Align Left"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h10M4 18h16" />
                            </svg>
                          </button>
                          
                          <button
                            type="button"
                            onClick={() => updateImageProperties(slideKey, idx, { float: 'none', position: 'center' })}
                            className={`p-2 rounded-md ${imageData.position === 'center' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}
                            title="Center"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M8 12h8M4 18h16" />
                            </svg>
                          </button>
                          
                          <button
                            type="button"
                            onClick={() => updateImageProperties(slideKey, idx, { float: 'right', position: 'right' })}
                            className={`p-2 rounded-md ${imageData.position === 'right' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}
                            title="Align Right"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M10 12h10M4 18h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      
                      {/* Spacing control */}
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Margin</label>
                        <select
                          value={imageData.margin}
                          onChange={(e) => updateImageProperties(slideKey, idx, { margin: e.target.value })}
                          className="block w-full pl-3 pr-10 py-1.5 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                        >
                          <option value="0px">None</option>
                          <option value="5px">Small (5px)</option>
                          <option value="10px">Medium (10px)</option>
                          <option value="20px">Large (20px)</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            
            {currentSlideImages.length === 0 && (
              <div className="border border-dashed rounded p-6 flex items-center justify-center text-gray-400">
                No images added
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`min-h-screen bg-gray-50 ${isFullScreen ? 'fixed inset-0 z-50 bg-black' : ''}`}>
      <div className={`container mx-auto px-4 py-6 ${isFullScreen ? 'h-full flex flex-col' : ''}`}>
        {!isFullScreen && (
          <div className="mb-8 flex flex-wrap items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">{pitchDeck.name}</h1>
              <p className="text-gray-600 mt-1">
                {product?.name} - {product?.development_stage}
              </p>
              
              {isEditing && (
                <div className="mt-2 flex items-center">
                  <span className="text-sm font-medium text-blue-600">Edit Mode</span>
                </div>
              )}
            </div>
            
            <div className="flex items-center space-x-3 mt-4 md:mt-0">
              {!isEditing ? (
                <>
                  <button
                    onClick={handleDownload}
                    className="flex items-center px-4 py-2 bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download
                  </button>
                  
                  {/* Post to Gallery Button */}
                  <PostToGalleryButton pitchDeck={pitchDeck} />
                  
                  {/* Toggle Edit Mode Button - Disable for uploaded documents */}
                  {!isUploadedDocument ? (
                    <button
                      onClick={() => {
                        // Get the slides data from either direct slides or from content.slides
                        const slidesData = pitchDeck.slides || (pitchDeck.content && pitchDeck.content.slides);
                        if (!slidesData) {
                          alert('No slide data found for this pitch deck');
                          return;
                        }
                        setEditableSlides(slidesData);
                        setIsEditing(true);
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Edit Pitch Deck
                    </button>
                  ) : (
                    <button
                      disabled
                      className="px-4 py-2 bg-gray-300 text-gray-500 rounded cursor-not-allowed"
                      title="Uploaded pitch decks cannot be edited. You can upload a new version instead."
                    >
                      Edit Pitch Deck
                    </button>
                  )}
                </>
              ) : (
                <>
                  <button
                    onClick={handleSavePitchDeck}
                    disabled={isSaving}
                    className={`px-4 py-2 rounded ${isSaving ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'} text-white`}
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                  
                  <button
                    onClick={() => setIsEditing(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  
                  {saveSuccess && (
                    <span className="text-green-600 text-sm">Changes saved successfully!</span>
                  )}
                </>
              )}
            </div>
          </div>
        )}
        
        {/* Background Image Settings (only in edit mode) */}
        {isEditing && !isFullScreen && (
          <div className="mb-6 bg-white rounded-lg shadow-sm p-4 border">
            <h3 className="text-lg font-medium mb-4">Background Settings</h3>
            
            <div className="flex flex-wrap gap-4 items-start">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Deck Background Image
                  </label>
                  <ImageUploader 
                    onImageUpload={(imageUrl) => setBackgroundImage(imageUrl)}
                    buttonText="Change Background"
                  />
                </div>
                
                <div className="border rounded p-2 bg-gray-50">
                  {editableSlides?.background_image ? (
                    <div className="relative group">
                      <img 
                        src={editableSlides.background_image} 
                        alt="Deck background" 
                        className="h-24 w-full object-cover rounded"
                      />
                      <button
                        onClick={() => setBackgroundImage('')}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove background"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div className="h-24 flex items-center justify-center text-gray-400">
                      No background image set
                    </div>
                  )}
                </div>
              </div>
              
              <div className="w-full md:w-auto">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Background Fit
                </label>
                <select
                  value={editableSlides?.background_fit || 'cover'}
                  onChange={(e) => setBackgroundFit(e.target.value)}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                >
                  <option value="cover">Cover (fill)</option>
                  <option value="contain">Contain (fit)</option>
                  <option value="auto">Auto (original)</option>
                  <option value="100% 100%">Stretch</option>
                </select>
              </div>
            </div>
          </div>
        )}
        
        {/* Presentation view */}
        <div className={`bg-white rounded-lg shadow-md ${isFullScreen ? 'flex-grow flex flex-col overflow-hidden' : 'p-6'}`}>
          {!isFullScreen && (
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Pitch Deck Presentation</h2>
              
              <div className="flex space-x-2">
                {!isEditing && (
                  <>
                    <button
                      onClick={toggleFullScreen}
                      className="p-2 rounded text-gray-600 hover:bg-gray-100"
                      title="Full Screen"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
                      </svg>
                    </button>
                    
                    {isPlaying ? (
                      <button
                        onClick={stopSlideshow}
                        className="p-2 rounded text-gray-600 hover:bg-gray-100"
                        title="Pause Slideshow"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 9v6m4-6v6m-9-3a9 9 0 1118 0 9 9 0 01-18 0z" />
                        </svg>
                      </button>
                    ) : (
                      <button
                        onClick={startSlideshow}
                        className="p-2 rounded text-gray-600 hover:bg-gray-100"
                        title="Start Slideshow"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
          
          {/* Slide content */}
          <div className={`flex flex-col items-center ${isFullScreen ? 'flex-grow justify-center' : 'border rounded-lg overflow-hidden'}`}>
            {isUploadedDocument && pitchDeck?.file_url ? (
              <div className="w-full h-full min-h-[500px]">
                <UploadedDocumentViewer 
                  fileUrl={pitchDeck.file_url}
                  fileType={pitchDeck.file_type}
                />
              </div>
            ) : totalSlides > 0 ? (
              <div 
                className={`w-full ${isFullScreen ? 'h-full flex flex-col' : 'aspect-video bg-white'}`}
                style={{
                  backgroundImage: !isEditing && (
                    pitchDeck.slides?.background_image ? 
                      `url(${pitchDeck.slides.background_image})` : 
                      (pitchDeck.content?.slides?.background_image ? 
                        `url(${pitchDeck.content.slides.background_image})` : 
                        'none')
                  ),
                  backgroundSize: pitchDeck.slides?.background_fit || 
                                  pitchDeck.content?.slides?.background_fit || 
                                  'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat'
                }}
              >
                <div className={`relative ${isFullScreen ? 'flex-grow' : ''}`}>
                  {/* Current slide */}
                  <div className={`w-full h-full p-8 ${isFullScreen ? 'flex flex-col justify-center items-center' : ''}`}>
                    {isEditing ? (
                      renderEditableSlide(currentSlideKey, currentSlideContent)
                    ) : currentSlideContent ? (
                      <>
                        <h3 className={`font-bold mb-4 ${isFullScreen ? 'text-3xl text-white' : 'text-xl'}`}>
                          {currentSlideContent.title || currentSlideKey}
                        </h3>
                        
                        {currentSlideContent.content && (
                          <div 
                            className={`prose max-w-none ${isFullScreen ? 'text-white text-xl' : ''}`}
                            dangerouslySetInnerHTML={{ __html: currentSlideContent.content }}
                          />
                        )}
                        
                        {currentSlideContent.images && currentSlideContent.images.length > 0 && (
                          <div className="mt-4 w-full">
                            {currentSlideContent.images.map((image, idx) => {
                              const imageUrl = typeof image === 'string' ? image : image.url;
                              const position = typeof image === 'string' ? 'center' : (image.position || 'center');
                              
                              // Create proper image styling that respects alignment
                              const imageStyle = typeof image === 'string' 
                                ? { maxWidth: '50%' } 
                                : {
                                    width: image.width || '50%',
                                    margin: image.margin || '10px',
                                    maxWidth: '100%'
                                  };
                              
                              return (
                                <div 
                                  key={idx} 
                                  className="mb-4"
                                  style={{ textAlign: position }}
                                >
                                  <img 
                                    src={imageUrl} 
                                    alt={`Slide ${idx + 1}`} 
                                    className="inline-block object-contain rounded-md shadow-sm"
                                    style={imageStyle}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-center p-12">No slide content available</div>
                    )}
                  </div>
                  
                  {/* Slide navigation controls */}
                  <div className={`${isFullScreen ? 'absolute bottom-8 left-0 right-0' : 'mt-4'} flex justify-between items-center px-4`}>
                    <button
                      onClick={handlePrevSlide}
                      disabled={currentSlide === 0}
                      className={`p-2 rounded-full ${isFullScreen ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-700'} hover:bg-opacity-80 ${currentSlide === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    
                    <div className={`text-sm ${isFullScreen ? 'text-white' : 'text-gray-600'}`}>
                      Slide {currentSlide + 1} of {totalSlides}
                    </div>
                    
                    <button
                      onClick={handleNextSlide}
                      disabled={currentSlide === totalSlides - 1}
                      className={`p-2 rounded-full ${isFullScreen ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-700'} hover:bg-opacity-80 ${currentSlide === totalSlides - 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                  
                  {/* Exit fullscreen button */}
                  {isFullScreen && (
                    <button
                      onClick={toggleFullScreen}
                      className="absolute top-4 right-4 p-2 rounded-full bg-white/20 text-white hover:bg-opacity-80"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="p-12 text-center">
                <p>This pitch deck has no slides yet.</p>
              </div>
            )}
          </div>
          
          {/* Slide thumbnails for easy navigation (only in non-fullscreen mode) */}
          {!isFullScreen && totalSlides > 0 && (
            <div className="mt-6">
              <h3 className="text-md font-medium mb-3">All Slides</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {orderedSlideKeys.map((slideKey, index) => {
                  const slide = pitchDeck.slides ? 
                    pitchDeck.slides[slideKey] : 
                    (pitchDeck.content && pitchDeck.content.slides ? 
                      pitchDeck.content.slides[slideKey] : 
                      {});
                  return (
                    <div 
                      key={slideKey}
                      onClick={() => setCurrentSlide(index)}
                      className={`border rounded cursor-pointer overflow-hidden transition ${currentSlide === index ? 'ring-2 ring-blue-500' : 'hover:border-blue-300'}`}
                    >
                      <div className="relative aspect-video">
                        {slide.images && slide.images[0] && (
                          <img 
                            src={typeof slide.images[0] === 'string' ? slide.images[0] : slide.images[0].url} 
                            alt={slide.title || slideKey} 
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      <div className="p-3 bg-gray-50 text-center">
                        <h4 className="text-sm font-medium truncate">{slide.title || slideKey}</h4>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PitchDeckViewPage;